import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, errMsg } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import { Card, PageHeader } from "../components/common/UI";
import { StatusBadge } from "../components/common/Badges";

const KPIS = [
  ["total", "Total measures", "/registry/all"],
  ["proposed_ordinances", "Proposed ordinances", "/registry/ordinances?numbered=no"],
  ["proposed_resolutions", "Proposed resolutions", "/registry/resolutions?numbered=no"],
  ["pending_committee", "Pending committee action", "/registry/all?stage_category=committee"],
  ["awaiting_session", "In session pipeline", "/registry/all?stage_category=session"],
  ["awaiting_executive", "Executive action stage", "/registry/all?stage_category=executive"],
  ["under_external_review", "Under external review", "/registry/all?stage_category=review"],
  ["awaiting_publication", "Publication / posting stage", "/registry/all?stage_category=publication"],
  ["enacted_ordinances", "Numbered ordinances", "/registry/ordinances?numbered=yes"],
  ["adopted_resolutions", "Numbered resolutions", "/registry/resolutions?numbered=yes"],
  ["overdue_tasks", "Overdue tasks", "/tasks?status=overdue"],
  ["updated_last_7_days", "Updated in last 7 days", "/registry/all?sort=updated_at"],
];
const COLORS = ["#0284C7", "#0EA5E9", "#0F172A", "#38BDF8", "#475569", "#7DD3FC", "#94A3B8"];

const HBar = ({ data, onPick, dataKey = "count", testId }) => (
  <div data-testid={testId} style={{ height: Math.max(160, data.length * 30) }}>
    <ResponsiveContainer>
      <BarChart data={data} layout="vertical" margin={{ left: 10, right: 16 }}>
        <CartesianGrid horizontal={false} stroke="#E2E8F0" />
        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
        <YAxis type="category" dataKey="name" width={170} tick={{ fontSize: 11 }} />
        <Tooltip />
        <Bar dataKey={dataKey} fill="#0284C7" radius={[0, 3, 3, 0]} cursor={onPick ? "pointer" : undefined} onClick={(d) => onPick && onPick(d.payload || d)} />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const nav = useNavigate();
  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data)).catch((e) => setError(errMsg(e)));
  }, []);
  if (error) return <div className="text-sm text-red-600">{error}</div>;
  if (!data) return <div className="text-sm text-slate-500">Loading dashboard…</div>;
  const c = data.charts;
  const drill = (param) => (d) => d.key && nav(`/registry/all?${param}=${encodeURIComponent(d.key)}`);
  return (
    <div className="fade-up">
      <PageHeader eyebrow="Legislative Dashboard" title="Overview" subtitle="Live figures from the legislative registry. Select any card or bar to open the matching records." />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {KPIS.map(([k, l, to], i) => (
          <Link key={k} to={to} data-testid={`kpi-card-${k}`} style={{ animationDelay: `${i * 30}ms` }}
            className="fade-up group rounded-md border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:border-sky-300 hover:shadow">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{l}</div>
            <div className={`mt-2 font-heading text-3xl font-semibold ${k === "overdue_tasks" && data.kpis[k] > 0 ? "text-red-600" : "text-slate-900"}`} data-testid={`kpi-value-${k}`}>
              {data.kpis[k]}
            </div>
          </Link>
        ))}
      </div>
      <div className="mt-6 grid gap-6 xl:grid-cols-3">
        <Card title="Measures filed by month" className="xl:col-span-2" testId="chart-by-month">
          <div style={{ height: 240 }}>
            <ResponsiveContainer>
              <AreaChart data={c.by_month}>
                <CartesianGrid stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Area type="monotone" dataKey="count" stroke="#0284C7" fill="#E0F2FE" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="By measure type" testId="chart-by-type">
          <div style={{ height: 240 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={c.by_type} dataKey="count" nameKey="name" innerRadius={50} outerRadius={85} onClick={(d) => drill("measure_type_id")(d.payload || d)} cursor="pointer">
                  {c.by_type.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            {c.by_type.map((t, i) => (
              <span key={t.key} className="flex items-center gap-1.5 text-[11px] text-slate-600">
                <span className="h-2 w-2 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} /> {t.name} ({t.count})
              </span>
            ))}
          </div>
        </Card>
        <Card title="By workflow status" testId="chart-by-status"><HBar data={c.by_status} onPick={drill("status")} /></Card>
        <Card title="By committee" testId="chart-by-committee"><HBar data={c.by_committee.filter((d) => d.key)} onPick={drill("committee_id")} /></Card>
        <Card title="By principal sponsor" testId="chart-by-sponsor"><HBar data={c.by_sponsor.filter((d) => d.key)} onPick={drill("sponsor_id")} /></Card>
        <Card title="By legislative term" testId="chart-by-term"><HBar data={c.by_term} onPick={drill("term_id")} /></Card>
        <Card title="Avg. days from filing to number assignment" testId="chart-processing">
          {c.processing_days.length ? <HBar data={c.processing_days} dataKey="avg_days" /> : <div className="py-8 text-center text-xs text-slate-400">No numbered measures with filing dates yet.</div>}
        </Card>
        <Card title="Recently updated" testId="recent-measures">
          <ul className="divide-y divide-slate-100">
            {data.recent.map((m) => (
              <li key={m.id} className="py-2">
                <Link to={`/measures/${m.id}`} className="block hover:text-sky-700" data-testid={`recent-measure-${m.id}`}>
                  <div className="line-clamp-1 text-xs font-medium text-slate-800">{m.official_number || m.tracking_ref} — {m.proposed_title}</div>
                  <div className="mt-1 flex items-center gap-2"><StatusBadge category={m.stage_category} name={m.stage_name} /><span className="text-[10px] text-slate-400">{fmtDateTime(m.updated_at)}</span></div>
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}
